import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Grid2, Paper, TextField, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
    DialogActions, Alert, Snackbar, Chip
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Event as EventIcon
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ModernNavbar from '../setup/ModernNavbar';
import HeadingTest from '../../BuiltInComponent/HeadingTest';
import {
    getGazettedHolidays,
    addGazettedHoliday,
    updateGazettedHoliday,
    deleteGazettedHoliday,
    clearError,
    clearSuccess
} from '../../features/AdminManagement/gazettedHolidaysSlice';
import { checkPeriodLock } from '../../features/PayrollManagement/salarySlice';

const GazettedHoliday = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { holidays, loading, error, success } = useSelector((state) => state.gazettedHolidays);

    const [formDialog, setFormDialog] = useState({ open: false, mode: 'add', holiday: null });
    const [formData, setFormData] = useState({
        holidayName: '',
        holidayDate: null,
        holidayEndDate: null,
        description: ''
    });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [periodLocked, setPeriodLocked] = useState(false);
    const [lockMessage, setLockMessage] = useState('');
    const [selectedUnitID, setSelectedUnitID] = useState(1);

    useEffect(() => {
        dispatch(getGazettedHolidays());
    }, [dispatch, success]);

    // Check if period is locked when holiday date changes
    useEffect(() => {
        if (formData.holidayDate) {
            const holidayDate = dayjs(formData.holidayDate).toDate();
            const month = holidayDate.getMonth() + 1;
            const year = holidayDate.getFullYear();

            dispatch(checkPeriodLock({
                unitID: selectedUnitID,
                selectedYear: year,
                selectedMonth: month
            })).then((res) => {
                if (res.payload?.isLocked) {
                    setPeriodLocked(true);
                    setLockMessage(res.payload?.message || 'Period is locked');
                } else {
                    setPeriodLocked(false);
                    setLockMessage('');
                }
            });
        }
    }, [formData.holidayDate, selectedUnitID, dispatch]);

    useEffect(() => {
        if (success) {
            setSnackbar({ 
                open: true, 
                message: formDialog.mode === 'add' ? 'Holiday added successfully!' : 'Holiday updated successfully!', 
                severity: 'success' 
            });
            dispatch(clearSuccess());
            handleCloseDialog();
        }
    }, [success, dispatch, formDialog.mode]);

    useEffect(() => {
        if (error) {
            setSnackbar({ open: true, message: error, severity: 'error' });
            dispatch(clearError());
        }
    }, [error, dispatch]);

    const handleOpenDialog = (mode, holiday = null) => {
        if (mode === 'edit' && holiday) {
            setFormData({
                holidayName: holiday.holidayName,
                holidayDate: dayjs(holiday.holidayDate),
                holidayEndDate: holiday.holidayEndDate ? dayjs(holiday.holidayEndDate) : null,
                description: holiday.description || ''
            });
        } else {
            setFormData({
                holidayName: '',
                holidayDate: null,
                holidayEndDate: null,
                description: ''
            });
        }
        setFormDialog({ open: true, mode, holiday });
    };

    const handleCloseDialog = () => {
        setFormDialog({ open: false, mode: 'add', holiday: null });
        setFormData({
            holidayName: '',
            holidayDate: null,
            holidayEndDate: null,
            description: ''
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.holidayName || !formData.holidayDate) {
            setSnackbar({ open: true, message: 'Holiday name and date are required', severity: 'error' });
            return;
        }

        if (periodLocked) {
            setSnackbar({ open: true, message: `Cannot save holiday - ${lockMessage}`, severity: 'error' });
            return;
        }

        const submitData = {
            holidayName: formData.holidayName,
            holidayDate: dayjs(formData.holidayDate).format('YYYY-MM-DD'),
            holidayEndDate: formData.holidayEndDate ? dayjs(formData.holidayEndDate).format('YYYY-MM-DD') : null,
            description: formData.description,
            createdBy: user?.empID
        };

        if (formDialog.mode === 'add') {
            dispatch(addGazettedHoliday(submitData));
        } else {
            dispatch(updateGazettedHoliday({
                holidayID: formDialog.holiday.holidayID,
                holidayData: submitData
            }));
        }
    };

    const handleDelete = (holidayID, holidayDate) => {
        // Check if period is locked for this holiday
        const hDate = dayjs(holidayDate).toDate();
        const month = hDate.getMonth() + 1;
        const year = hDate.getFullYear();

        dispatch(checkPeriodLock({
            unitID: selectedUnitID,
            selectedYear: year,
            selectedMonth: month
        })).then((res) => {
            if (res.payload?.isLocked) {
                setSnackbar({ open: true, message: `Cannot delete - ${res.payload?.message}`, severity: 'error' });
                return;
            }
            if (window.confirm('Are you sure you want to delete this holiday?')) {
                dispatch(deleteGazettedHoliday(holidayID));
            }
        });
    };

    const isHolidayPast = (date) => {
        return dayjs(date).isBefore(dayjs(), 'day');
    };

    const isHolidayUpcoming = (date) => {
        return dayjs(date).isAfter(dayjs(), 'day');
    };

    const getHolidayDuration = (startDate, endDate) => {
        if (!endDate) return '1 day';
        const days = dayjs(endDate).diff(dayjs(startDate), 'day') + 1;
        return `${days} days`;
    };

    return (
        <>
            <ModernNavbar />
            <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
                <HeadingTest title="Gazetted Holidays Management" />

                {/* Add Holiday Button */}
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog('add')}
                        sx={{
                            bgcolor: '#1E90FF',
                            '&:hover': { bgcolor: '#1c86ee' },
                            px: 3
                        }}
                    >
                        Add New Holiday
                    </Button>
                </Box>

                {/* Holidays Table */}
                <Paper sx={{ borderRadius: 2, boxShadow: 3 }}>
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: '#1E90FF', fontWeight: 600 }}>
                            All Holidays
                        </Typography>

                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#1E90FF' }}>
                                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Holiday Name</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Start Date</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>End Date</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Duration</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Description</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Created By</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {holidays && holidays.length > 0 ? (
                                        holidays.map((holiday) => (
                                            <TableRow key={holiday.holidayID} hover>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <EventIcon sx={{ color: '#1E90FF' }} />
                                                        <Typography fontWeight={600}>{holiday.holidayName}</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{dayjs(holiday.holidayDate).format('DD-MMM-YYYY')}</TableCell>
                                                <TableCell>
                                                    {holiday.holidayEndDate 
                                                        ? dayjs(holiday.holidayEndDate).format('DD-MMM-YYYY') 
                                                        : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={getHolidayDuration(holiday.holidayDate, holiday.holidayEndDate)} 
                                                        size="small"
                                                        color="primary"
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {holiday.description || '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {isHolidayPast(holiday.holidayDate) ? (
                                                        <Chip label="Past" size="small" color="default" />
                                                    ) : isHolidayUpcoming(holiday.holidayDate) ? (
                                                        <Chip label="Upcoming" size="small" color="success" />
                                                    ) : (
                                                        <Chip label="Today" size="small" color="warning" />
                                                    )}
                                                </TableCell>
                                                <TableCell>{holiday.createdByName || '-'}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Tooltip title="Edit">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handleOpenDialog('edit', holiday)}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleDelete(holiday.holidayID, holiday.holidayDate)}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center">
                                                <Typography color="textSecondary">No holidays found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Paper>

                {/* Add/Edit Dialog */}
                <Dialog
                    open={formDialog.open}
                    onClose={handleCloseDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ bgcolor: '#1E90FF', color: 'white' }}>
                        {formDialog.mode === 'add' ? 'Add New Holiday' : 'Edit Holiday'}
                    </DialogTitle>
                    <DialogContent sx={{ mt: 2 }}>
                        <form onSubmit={handleSubmit}>
                            <Grid2 container spacing={2}>
                                <Grid2 size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Holiday Name"
                                        name="holidayName"
                                        value={formData.holidayName}
                                        onChange={handleChange}
                                        placeholder="e.g., Independence Day"
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12, md: 6 }}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label="Start Date"
                                            value={formData.holidayDate}
                                            onChange={(date) => setFormData(prev => ({ ...prev, holidayDate: date }))}
                                            slotProps={{ textField: { fullWidth: true, required: true } }}
                                        />
                                    </LocalizationProvider>
                                </Grid2>

                                <Grid2 size={{ xs: 12, md: 6 }}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label="End Date (Optional)"
                                            value={formData.holidayEndDate}
                                            onChange={(date) => setFormData(prev => ({ ...prev, holidayEndDate: date }))}
                                            minDate={formData.holidayDate}
                                            slotProps={{ textField: { fullWidth: true } }}
                                        />
                                    </LocalizationProvider>
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Description (Optional)"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        placeholder="Brief description about the holiday..."
                                    />
                                </Grid2>

                                {periodLocked && (
                                    <Grid2 size={{ xs: 12 }}>
                                        <Alert severity="warning">
                                            <strong>Period Locked:</strong> {lockMessage}
                                        </Alert>
                                    </Grid2>
                                )}
                            </Grid2>
                        </form>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog} color="inherit">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            disabled={loading || periodLocked}
                            sx={{
                                bgcolor: '#1E90FF',
                                '&:hover': { bgcolor: '#1c86ee' }
                            }}
                        >
                            {loading ? 'Saving...' : formDialog.mode === 'add' ? 'Add Holiday' : 'Update Holiday'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert
                        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </>
    );
};

export default GazettedHoliday;
